export const tokens = {
  color: {
    brand: {
      sage: '#D1DDB8',
      sageLight: '#E2EACB',
      sageDark: '#B8C89E',
      navy: '#1A1D2E',
      navyLight: '#232640',
      navyDark: '#121420',
      teal: '#86BFB8',
      tealLight: '#A8D5CF',
      tealDark: '#2D3B3A',
      lime: '#e7f1ab',
      limeLight: '#f0f7cb',
      limeDark: '#d4e47a',
      softWhite: '#f8f0ff',
      nearBlack: '#231f20',
    },
    surface: {
      light: '#f0ebe6',
      raised: '#f5f0eb',
      sunken: '#e8e3de',
      dark: '#1A1D2E',
      darkRaised: '#232640',
      darkSunken: '#121420',
      card: 'rgba(35, 38, 64, 0.85)',
    },
    accent: {
      xpGold: '#fbbf24',
      xpGoldGlow: 'rgba(251, 191, 36, 0.4)',
      xpPurple: '#a78bfa',
      xpPurpleGlow: 'rgba(167, 139, 250, 0.3)',
      streakFire: '#f97316',
      streakGlow: 'rgba(249, 115, 22, 0.4)',
      success: '#34d399',
      successGlow: 'rgba(52, 211, 153, 0.3)',
      danger: '#f87171',
      warning: '#fbbf24',
    },
    rank: {
      bronze: '#cd7f32',
      silver: '#c0c0c0',
      gold: '#ffd700',
      platinum: '#e5e4e2',
      diamond: '#b9f2ff',
    },
    glass: {
      background: 'rgba(34, 34, 64, 0.6)',
      border: 'rgba(248, 240, 255, 0.08)',
      highlight: 'rgba(248, 240, 255, 0.04)',
    },
  },
  gradient: {
    tier: {
      seedling: 'linear-gradient(135deg, #a0d996, #7ab96e)',
      sprout: 'linear-gradient(135deg, #4d6aad, #364c85)',
      bloom: 'linear-gradient(135deg, #a78bfa, #7c3aed)',
      sage: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
      elder: 'linear-gradient(135deg, #f472b6, #ec4899)',
    },
  },
  space: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '3rem',
    xxxl: '4rem',
  },
  radius: {
    sm: '8px',
    md: '14px',
    lg: '20px',
    xl: '28px',
    full: '50%',
    pill: '999px',
  },
  shadow: {
    neoLight: '6px 6px 12px rgba(0, 0, 0, 0.08), -6px -6px 12px rgba(255, 255, 255, 0.9)',
    neoLightSm: '3px 3px 6px rgba(0, 0, 0, 0.06), -3px -3px 6px rgba(255, 255, 255, 0.8)',
    neoInset: 'inset 3px 3px 6px rgba(0, 0, 0, 0.06), inset -3px -3px 6px rgba(255, 255, 255, 0.8)',
    neoDark: '6px 6px 16px rgba(0, 0, 0, 0.5), -6px -6px 16px rgba(255, 255, 255, 0.02)',
    neoDarkSm: '3px 3px 8px rgba(0, 0, 0, 0.4), -3px -3px 8px rgba(255, 255, 255, 0.015)',
    neoDarkInset: 'inset 3px 3px 8px rgba(0, 0, 0, 0.4), inset -3px -3px 8px rgba(255, 255, 255, 0.015)',
    glowSage: '0 0 20px rgba(160, 217, 150, 0.3), 0 0 40px rgba(160, 217, 150, 0.1)',
    glowNavy: '0 0 20px rgba(77, 106, 173, 0.3), 0 0 40px rgba(77, 106, 173, 0.1)',
    glowGold: '0 0 15px rgba(251, 191, 36, 0.4), 0 0 30px rgba(251, 191, 36, 0.15)',
    glowPurple: '0 0 15px rgba(167, 139, 250, 0.3), 0 0 30px rgba(167, 139, 250, 0.1)',
  },
  motion: {
    duration: {
      fast: '150ms',
      normal: '250ms',
      slow: '400ms',
      xslow: '600ms',
    },
    easing: {
      smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
      bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
      spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    },
  },
  font: {
    family: {
      display: "'Poppins', sans-serif",
      body: "'Raleway', sans-serif",
      brand: "'Besides', 'Georgia', serif",
    },
    weight: {
      regular: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
      black: '900',
    },
    size: {
      h1: '1.75rem',
      h2: '1.35rem',
      h3: '1.1rem',
      body: '0.95rem',
      label: '0.85rem',
      micro: '0.75rem',
    },
    lineHeight: {
      tight: '1.2',
      body: '1.6',
      relaxed: '1.7',
    },
  },
  effect: {
    glassBlur: 'blur(20px)',
  },
  breakpoint: {
    sm: '480px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
  },
} as const;

export const legacyTokenAliases = {
  '--sage-green': 'color.brand.sage',
  '--sage-light': 'color.brand.sageLight',
  '--sage-dark': 'color.brand.sageDark',
  '--deep-navy': 'color.brand.navy',
  '--navy-light': 'color.brand.navyLight',
  '--navy-dark': 'color.brand.navyDark',
  '--lime-cream': 'color.brand.lime',
  '--lime-light': 'color.brand.limeLight',
  '--lime-dark': 'color.brand.limeDark',
  '--soft-white': 'color.brand.softWhite',
  '--near-black': 'color.brand.nearBlack',
  '--surface-bg': 'color.surface.light',
  '--surface-raised': 'color.surface.raised',
  '--surface-sunken': 'color.surface.sunken',
  '--surface-dark': 'color.surface.dark',
  '--surface-dark-raised': 'color.surface.darkRaised',
  '--surface-dark-sunken': 'color.surface.darkSunken',
  '--surface-card': 'color.surface.card',
  '--xp-gold': 'color.accent.xpGold',
  '--xp-gold-glow': 'color.accent.xpGoldGlow',
  '--xp-purple': 'color.accent.xpPurple',
  '--xp-purple-glow': 'color.accent.xpPurpleGlow',
  '--streak-fire': 'color.accent.streakFire',
  '--streak-glow': 'color.accent.streakGlow',
  '--success-green': 'color.accent.success',
  '--success-glow': 'color.accent.successGlow',
  '--danger-red': 'color.accent.danger',
  '--warning-amber': 'color.accent.warning',
  '--rank-bronze': 'color.rank.bronze',
  '--rank-silver': 'color.rank.silver',
  '--rank-gold': 'color.rank.gold',
  '--rank-platinum': 'color.rank.platinum',
  '--rank-diamond': 'color.rank.diamond',
  '--tier-seedling': 'gradient.tier.seedling',
  '--tier-sprout': 'gradient.tier.sprout',
  '--tier-bloom': 'gradient.tier.bloom',
  '--tier-sage': 'gradient.tier.sage',
  '--tier-elder': 'gradient.tier.elder',
  '--glass-bg': 'color.glass.background',
  '--glass-border': 'color.glass.border',
  '--glass-highlight': 'color.glass.highlight',
  '--glass-blur': 'effect.glassBlur',
  '--neo-shadow-light': 'shadow.neoLight',
  '--neo-shadow-light-sm': 'shadow.neoLightSm',
  '--neo-shadow-inset': 'shadow.neoInset',
  '--neo-dark-shadow': 'shadow.neoDark',
  '--neo-dark-shadow-sm': 'shadow.neoDarkSm',
  '--neo-dark-inset': 'shadow.neoDarkInset',
  '--glow-sage': 'shadow.glowSage',
  '--glow-navy': 'shadow.glowNavy',
  '--glow-gold': 'shadow.glowGold',
  '--glow-purple': 'shadow.glowPurple',
  '--space-xs': 'space.xs',
  '--space-sm': 'space.sm',
  '--space-md': 'space.md',
  '--space-lg': 'space.lg',
  '--space-xl': 'space.xl',
  '--space-2xl': 'space.xxl',
  '--space-3xl': 'space.xxxl',
  '--radius-sm': 'radius.sm',
  '--radius-md': 'radius.md',
  '--radius-lg': 'radius.lg',
  '--radius-xl': 'radius.xl',
  '--radius-full': 'radius.full',
  '--radius-pill': 'radius.pill',
  '--ease-smooth': 'motion.easing.smooth',
  '--ease-bounce': 'motion.easing.bounce',
  '--ease-spring': 'motion.easing.spring',
  '--duration-fast': 'motion.duration.fast',
  '--duration-normal': 'motion.duration.normal',
  '--duration-slow': 'motion.duration.slow',
  '--duration-xslow': 'motion.duration.xslow',
} as const;

export type DesignTokens = typeof tokens;
